import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from 'recharts';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useSelectedUser } from '../hooks/useSelectedUser';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { fmtDuration } from '../lib/formatters';
import { Monitor, Clock, Layers, Zap, Loader2, BarChart2 } from 'lucide-react';

// ─── helpers ──────────────────────────────────────────────────────────────────

function focusScore(totalActiveSec, categories) {
  if (!totalActiveSec) return 0;
  const workSec = categories
    .filter((c) => c.category?.startsWith('Work'))
    .reduce((s, c) => s + c.durationSec, 0);
  return Math.round((workSec / totalActiveSec) * 100);
}

// Colour palette for charts — stable by index
const PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
];

const CATEGORY_COLORS = {
  'Work > Programming':       '#3b82f6',
  'Work > Dev Tools':         '#6366f1',
  'Work > Design':            '#8b5cf6',
  'Work > Documents':         '#a78bfa',
  'Work > Project Management':'#60a5fa',
  'Comms > Video Conferencing':'#10b981',
  'Comms > Messaging':        '#34d399',
  'Comms > Email':            '#6ee7b7',
  'Media > Video':            '#f59e0b',
  'Media > Music':            '#fbbf24',
  'Media > Games':            '#f97316',
  'Media > Social Media':     '#ef4444',
  'Other':                    '#9ca3af',
};

function catColor(category) {
  return CATEGORY_COLORS[category] || '#9ca3af';
}

// ─── sub-components ───────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color = 'blue' }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
      <div className={`p-3 rounded-lg ${colorMap[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-xl font-bold text-gray-900">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function TimerOverlapBadge({ trackedDurationSec, compact = false }) {
  if (!trackedDurationSec) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-green-50 text-green-700 ${
        compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
      } font-medium`}
      title={`Timer active for ${fmtDuration(trackedDurationSec)} during this passive activity`}
    >
      <span className="h-2 w-2 rounded-full bg-green-500" />
      Timer on
    </span>
  );
}

function TopAppsChart({ apps }) {
  if (!apps?.length) return <p className="text-sm text-gray-400 text-center py-8">No data</p>;
  const max = apps[0]?.durationSec || 1;
  return (
    <div className="space-y-2">
      {apps.map((app, i) => (
        <div key={app.appName} className="flex items-center gap-3">
          <div className="w-40 shrink-0">
            <div className="flex items-center gap-2">
              <div className="min-w-0 text-sm text-gray-700 truncate" title={app.appName}>
                {app.appName}
              </div>
              <TimerOverlapBadge trackedDurationSec={app.trackedDurationSec} compact />
            </div>
            {app.trackedDurationSec > 0 && (
              <p className="text-[11px] text-green-600 mt-0.5">
                {fmtDuration(app.trackedDurationSec)} with timer active
              </p>
            )}
          </div>
          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className="h-3 rounded-full transition-all"
              style={{
                width: `${(app.durationSec / max) * 100}%`,
                backgroundColor: PALETTE[i % PALETTE.length],
              }}
            />
          </div>
          <div className="text-sm text-gray-500 w-16 text-right shrink-0">
            {fmtDuration(app.durationSec)}
          </div>
        </div>
      ))}
    </div>
  );
}

function CategoryDonut({ categories }) {
  if (!categories?.length) return <p className="text-sm text-gray-400 text-center py-8">No data</p>;

  const data = categories.slice(0, 8).map((c) => ({
    name: c.category || 'Other',
    value: c.durationSec,
    color: catColor(c.category),
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow text-sm">
        <p className="font-medium text-gray-800">{payload[0].name}</p>
        <p className="text-gray-500">{fmtDuration(payload[0].value)}</p>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={240}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={55}
          outerRadius={90}
          paddingAngle={2}
          dataKey="value"
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend
          formatter={(value) => <span className="text-xs text-gray-600">{value}</span>}
          iconSize={10}
          iconType="circle"
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function TopDomains({ domains }) {
  if (!domains?.length) return <p className="text-sm text-gray-400 text-center py-8">No domain data — browsing not detected or privacy mode on</p>;
  const max = domains[0]?.durationSec || 1;
  return (
    <div className="space-y-2">
      {domains.map((d, i) => (
        <div key={d.domain} className="flex items-center gap-3">
          <div className="w-48 shrink-0">
            <div className="flex items-center gap-2">
              <div className="min-w-0 text-sm text-gray-700 truncate" title={d.domain}>
                {d.domain}
              </div>
              <TimerOverlapBadge trackedDurationSec={d.trackedDurationSec} compact />
            </div>
            {d.trackedDurationSec > 0 && (
              <p className="text-[11px] text-green-600 mt-0.5">
                {fmtDuration(d.trackedDurationSec)} with timer active
              </p>
            )}
          </div>
          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className="h-3 rounded-full transition-all"
              style={{
                width: `${(d.durationSec / max) * 100}%`,
                backgroundColor: PALETTE[(i + 4) % PALETTE.length],
              }}
            />
          </div>
          <div className="text-sm text-gray-500 w-16 text-right shrink-0">
            {fmtDuration(d.durationSec)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── date range presets ───────────────────────────────────────────────────────

const PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This week', value: 'week' },
  { label: 'Last 7 days', value: '7d' },
];

function presetToDates(preset) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];

  if (preset === 'today') return { from: fmt(today), to: fmt(today) };
  if (preset === 'yesterday') {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    return { from: fmt(y), to: fmt(y) };
  }
  if (preset === 'week') {
    const start = new Date(today);
    start.setDate(today.getDate() - today.getDay());
    return { from: fmt(start), to: fmt(today) };
  }
  if (preset === '7d') {
    const start = new Date(today); start.setDate(today.getDate() - 6);
    return { from: fmt(start), to: fmt(today) };
  }
  return { from: fmt(today), to: fmt(today) };
}

// ─── user picker (admin/manager only) ────────────────────────────────────────

function UserPicker({ users, selectedUserId, onSelect }) {
  if (!users?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {users.map((u) => (
        <button
          key={u.id}
          onClick={() => onSelect(u.id)}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            selectedUserId === u.id
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {u.name}
        </button>
      ))}
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function Activity() {
  const { user } = useAuth();
  useDisplaySettings();
  const [selectedUserId, setSelectedUserId] = useSelectedUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const preset = searchParams.get('preset') || 'today';
  const { from, to } = useMemo(() => presetToDates(preset), [preset]);

  function setPreset(p) {
    setSearchParams((prev) => { prev.set('preset', p); return prev; });
  }

  const isManager = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => api.get('/api/users').then((r) => r.data?.data || r.data),
    enabled: isManager,
  });
  const users = usersData || [];

  const targetUserId = isManager ? selectedUserId : user?.id;

  const { data: summaryData, isLoading, isError } = useQuery({
    queryKey: ['app-sessions-summary', targetUserId, from, to],
    queryFn: () =>
      api.get('/api/app-sessions/summary', { params: { from, to, userId: targetUserId } })
        .then((r) => r.data?.data),
    enabled: !!targetUserId,
  });

  const totalActiveSec = summaryData?.totalActiveSec || 0;
  const totalTrackedSec = summaryData?.totalTrackedSec || 0;
  const sessionCount = summaryData?.sessionCount || 0;
  const trackedSessionCount = summaryData?.trackedSessionCount || 0;
  const topApps = summaryData?.topApps || [];
  const topDomains = summaryData?.topDomains || [];
  const categories = summaryData?.categories || [];
  const focus = focusScore(totalActiveSec, categories);


  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Activity</h1>
        </div>

        {/* Preset tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPreset(p.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                preset === p.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* User picker */}
      {isManager && users.length > 0 && (
        <UserPicker users={users} selectedUserId={selectedUserId} onSelect={setSelectedUserId} />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-600">
          Failed to load activity data. Please try again.
        </div>
      )}

      {/* Content */}
      {!isLoading && !isError && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Clock} label="Active Time" value={fmtDuration(totalActiveSec)} color="blue" />
            <StatCard icon={Layers} label="Sessions" value={sessionCount} color="yellow" />
            <StatCard
              icon={Monitor}
              label="Timer Overlap"
              value={fmtDuration(totalTrackedSec)}
              sub={trackedSessionCount > 0 ? `${trackedSessionCount} passive sessions while timer was running` : 'No passive overlap with timer yet'}
              color="green"
            />
            <StatCard
              icon={Zap}
              label="Focus Score"
              value={`${focus}%`}
              sub="Time in Work categories"
              color="purple"
            />
          </div>

          {/* No data */}
          {totalActiveSec === 0 && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-500">
              No activity recorded for this period. Make sure the Electron agent is running.
            </div>
          )}

          {totalActiveSec > 0 && (
            <>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                Green marker means the desktop timer was active during that passive activity.
              </div>

              {/* Top apps + Category donut */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="text-sm font-semibold text-gray-700 mb-4">Top Applications</h2>
                  <TopAppsChart apps={topApps} />
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="text-sm font-semibold text-gray-700 mb-2">Category Breakdown</h2>
                  <CategoryDonut categories={categories} />
                </div>
              </div>

              {/* Top Domains */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-4">Top Browser Domains</h2>
                <TopDomains domains={topDomains} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
